import React, { useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useResponsive } from '../../hooks/useResponsive';
import { X, Download, Printer, Copy, Link as LinkIcon, QrCode } from 'lucide-react';

function QRCodeModal({ manager, onClose }) {
  const qrRef = useRef(null);
  const { isMobile } = useResponsive();

  const handleDownload = () => {
    const svg = qrRef.current.querySelector('svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    canvas.width = 300;
    canvas.height = 300;
    
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 300, 300);
      ctx.drawImage(img, 0, 0, 300, 300);
      
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `QR-${manager.manager_name}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const handlePrint = () => {
    const printWindow = window.open('', '', 'width=600,height=600');
    const qrContent = qrRef.current.innerHTML;
    
    printWindow.document.write(`
      <html>
        <head>
          <title>QR Code - ${manager.manager_name}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 40px;
              font-family: Arial, sans-serif;
            }
            h2 { margin-bottom: 10px; }
            p { color: #666; margin-bottom: 30px; }
            .qr-container { 
              padding: 30px;
              background: #f9f9f9;
              border-radius: 12px;
            }
          </style>
        </head>
        <body>
          <h2>QR Code - ${manager.manager_name}</h2>
          <p>Scan this QR code to collect reviews</p>
          <div class="qr-container">${qrContent}</div>
          <p style="margin-top: 30px;">${manager.review_link}</p>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(manager.review_link);
    // Using native browser notification
    const notification = document.createElement('div');
    notification.textContent = '✓ URL copied!';
    notification.style.cssText = `
      position: fixed;
      top: ${isMobile ? '16px' : '24px'};
      right: ${isMobile ? '16px' : '24px'};
      left: ${isMobile ? '16px' : 'auto'};
      background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
      color: white;
      padding: ${isMobile ? '14px 20px' : '16px 24px'};
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(0,0,0,0.2);
      z-index: 99999;
      font-weight: 600;
      font-size: ${isMobile ? '14px' : '15px'};
    `;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 2000);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: isMobile ? '16px' : '20px',
      overflowY: 'auto'
    }} onClick={onClose}>
      <div 
        className="card" 
        style={{ 
          width: '100%',
          maxWidth: isMobile ? '100%' : '600px',
          margin: isMobile ? '0' : 'auto',
          position: 'relative',
          background: 'white',
          padding: isMobile ? '20px' : '28px',
          maxHeight: isMobile ? '95vh' : 'auto',
          overflowY: isMobile ? 'auto' : 'visible'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: '12px',
          paddingBottom: '16px',
          borderBottom: '2px solid #e8ecf1',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '12px', flex: 1 }}>
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: isMobile ? '8px' : '10px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <QrCode size={isMobile ? 20 : 24} color="white" />
            </div>
            <h2 style={{ 
              margin: 0, 
              fontSize: isMobile ? '18px' : '24px', 
              fontWeight: '700', 
              color: '#2c3e50',
              wordBreak: 'break-word'
            }}>QR Code - {manager.manager_name}</h2>
          </div>
          <button 
            onClick={onClose}
            style={{
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              border: '2px solid #e8ecf1',
              borderRadius: '10px',
              cursor: 'pointer',
              color: '#495057',
              padding: isMobile ? '6px' : '8px',
              display: 'flex',
              alignItems: 'center',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)';
              e.currentTarget.style.color = '#c62828';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
              e.currentTarget.style.color = '#495057';
            }}
          >
            <X size={isMobile ? 18 : 20} />
          </button>
        </div>

        <p style={{ 
          color: '#7f8c8d', 
          marginBottom: isMobile ? '20px' : '24px', 
          fontSize: isMobile ? '14px' : '15px' 
        }}>
          Scan this QR code or share the URL to collect reviews
        </p>

        {/* QR Code */}
        <div 
          ref={qrRef}
          style={{ 
            background: '#f9f9f9',
            padding: isMobile ? '20px' : '40px',
            borderRadius: '12px',
            display: 'flex',
            justifyContent: 'center',
            marginBottom: isMobile ? '20px' : '24px'
          }}
        >
          <QRCodeSVG 
            value={manager.review_link} 
            size={isMobile ? 200 : 300}
            level="H"
            includeMargin={true}
          />
        </div>

        {/* Review URL */}
        <div style={{ marginBottom: isMobile ? '20px' : '24px' }}>
          <label style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            marginBottom: '10px',
            color: '#7f8c8d',
            fontSize: isMobile ? '13px' : '14px',
            fontWeight: '600'
          }}>
            <LinkIcon size={isMobile ? 14 : 16} />
            <span>Review URL</span>
          </label>
          <div style={{ 
            display: 'flex', 
            gap: '8px',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            <input
              type="text"
              value={manager.review_link}
              readOnly
              style={{ 
                flex: 1,
                padding: isMobile ? '10px' : '12px',
                border: '2px solid #e8ecf1',
                borderRadius: '10px',
                fontSize: isMobile ? '13px' : '14px',
                marginBottom: 0,
                background: '#fafbfc'
              }}
            />
            <button 
              className="btn"
              style={{ 
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: 'white',
                padding: isMobile ? '10px 18px' : '12px 20px',
                minWidth: isMobile ? 'auto' : '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontWeight: '600',
                border: 'none',
                fontSize: isMobile ? '14px' : '15px'
              }}
              onClick={copyUrl}
            >
              <Copy size={isMobile ? 16 : 18} />
              <span>Copy</span>
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '12px',
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <button 
            className="btn btn-primary"
            style={{ 
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: isMobile ? '12px' : '14px',
              fontWeight: '600',
              fontSize: isMobile ? '14px' : '15px'
            }}
            onClick={handleDownload}
          >
            <Download size={isMobile ? 18 : 20} />
            <span>Download QR</span>
          </button>
          <button 
            className="btn"
            style={{ 
              flex: 1,
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              color: '#495057',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: isMobile ? '12px' : '14px',
              fontWeight: '600',
              fontSize: isMobile ? '14px' : '15px',
              border: '2px solid #e8ecf1'
            }}
            onClick={handlePrint}
          >
            <Printer size={isMobile ? 18 : 20} />
            <span>Print</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default QRCodeModal;

//   const handleDownload = () => {
//     const svg = qrRef.current.querySelector('svg');
//     const svgData = new XMLSerializer().serializeToString(svg);
//     const canvas = document.createElement('canvas');
//     const ctx = canvas.getContext('2d');
//     const img = new Image();
    
//     canvas.width = 300;
//     canvas.height = 300;
    
//     img.onload = () => {
//       ctx.fillStyle = 'white';
//       ctx.fillRect(0, 0, 300, 300);
//       ctx.drawImage(img, 0, 0, 300, 300);
      
//       const pngFile = canvas.toDataURL('image/png');
//       const downloadLink = document.createElement('a');
//       downloadLink.download = `QR-${manager.manager_name}.png`;
//       downloadLink.href = pngFile;
//       downloadLink.click();
//     };
    
//     img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
//   };

//   const handlePrint = () => {
//     const printWindow = window.open('', '', 'width=600,height=600');
//     const qrContent = qrRef.current.innerHTML;
    
//     printWindow.document.write(`
//       <html>
//         <head>
//           <title>QR Code - ${manager.manager_name}</title>
//           <style>
//             body {
//               display: flex;
//               flex-direction: column;
//               align-items: center;
//               justify-content: center;
//               padding: 40px;
//               font-family: Arial, sans-serif;
//             }
//             h2 { margin-bottom: 10px; }
//             p { color: #666; margin-bottom: 30px; }
//             .qr-container { 
//               padding: 30px;
//               background: #f9f9f9;
//               border-radius: 12px;
//             }
//           </style>
//         </head>
//         <body>
//           <h2>QR Code - ${manager.manager_name}</h2>
//           <p>Scan this QR code to collect reviews</p>
//           <div class="qr-container">${qrContent}</div>
//           <p style="margin-top: 30px;">${manager.review_link}</p>
//         </body>
//       </html>
//     `);
    
//     printWindow.document.close();
//     setTimeout(() => {
//       printWindow.print();
//       printWindow.close();
//     }, 250);
//   };

//   const copyUrl = () => {
//     navigator.clipboard.writeText(manager.review_link);
//     // Using native browser notification
//     const notification = document.createElement('div');
//     notification.textContent = '✓ URL copied!';
//     notification.style.cssText = `
//       position: fixed;
//       top: 24px;
//       right: 24px;
//       background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
//       color: white;
//       padding: 16px 24px;
//       border-radius: 12px;
//       box-shadow: 0 8px 24px rgba(0,0,0,0.2);
//       z-index: 99999;
//       font-weight: 600;
//     `;
//     document.body.appendChild(notification);
//     setTimeout(() => notification.remove(), 2000);
//   };

//   return (
//     <div style={{
//       position: 'fixed',
//       top: 0,
//       left: 0,
//       right: 0,
//       bottom: 0,
//       background: 'rgba(0,0,0,0.7)',
//       display: 'flex',
//       alignItems: 'center',
//       justifyContent: 'center',
//       zIndex: 10000,
//       padding: '20px'
//     }} onClick={onClose}>
//       <div 
//         className="card" 
//         style={{ 
//           width: '100%',
//           maxWidth: '600px',
//           margin: '0',
//           position: 'relative',
//           background: 'white'
//         }}
//         onClick={(e) => e.stopPropagation()}
//       >
//         {/* Header */}
//         <div style={{ 
//           display: 'flex', 
//           alignItems: 'center', 
//           justifyContent: 'space-between',
//           marginBottom: '12px',
//           paddingBottom: '16px',
//           borderBottom: '2px solid #e8ecf1'
//         }}>
//           <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
//             <div style={{
//               background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
//               padding: '10px',
//               borderRadius: '10px',
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center'
//             }}>
//               <QrCode size={24} color="white" />
//             </div>
//             <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#2c3e50' }}>QR Code - {manager.manager_name}</h2>
//           </div>
//           <button 
//             onClick={onClose}
//             style={{
//               background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
//               border: '2px solid #e8ecf1',
//               borderRadius: '10px',
//               cursor: 'pointer',
//               color: '#495057',
//               padding: '8px',
//               display: 'flex',
//               alignItems: 'center',
//               transition: 'all 0.3s'
//             }}
//             onMouseEnter={(e) => {
//               e.currentTarget.style.background = 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)';
//               e.currentTarget.style.color = '#c62828';
//             }}
//             onMouseLeave={(e) => {
//               e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)';
//               e.currentTarget.style.color = '#495057';
//             }}
//           >
//             <X size={20} />
//           </button>
//         </div>

//         <p style={{ color: '#7f8c8d', marginBottom: '24px', fontSize: '15px' }}>
//           Scan this QR code or share the URL to collect reviews
//         </p>

//         {/* QR Code */}
//         <div 
//           ref={qrRef}
//           style={{ 
//             background: '#f9f9f9',
//             padding: '40px',
//             borderRadius: '12px',
//             display: 'flex',
//             justifyContent: 'center',
//             marginBottom: '24px'
//           }}
//         >
//           <QRCodeSVG 
//             value={manager.review_link} 
//             size={300}
//             level="H"
//             includeMargin={true}
//           />
//         </div>

//         {/* Review URL */}
//         <div style={{ marginBottom: '24px' }}>
//           <label style={{ 
//             display: 'flex',
//             alignItems: 'center',
//             gap: '6px',
//             marginBottom: '10px',
//             color: '#7f8c8d',
//             fontSize: '14px',
//             fontWeight: '600'
//           }}>
//             <LinkIcon size={16} />
//             <span>Review URL</span>
//           </label>
//           <div style={{ display: 'flex', gap: '8px' }}>
//             <input
//               type="text"
//               value={manager.review_link}
//               readOnly
//               style={{ 
//                 flex: 1,
//                 padding: '12px',
//                 border: '2px solid #e8ecf1',
//                 borderRadius: '10px',
//                 fontSize: '14px',
//                 marginBottom: 0,
//                 background: '#fafbfc'
//               }}
//             />
//             <button 
//               className="btn"
//               style={{ 
//                 background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
//                 color: 'white',
//                 padding: '12px 20px',
//                 minWidth: '80px',
//                 display: 'flex',
//                 alignItems: 'center',
//                 gap: '8px',
//                 fontWeight: '600',
//                 border: 'none'
//               }}
//               onClick={copyUrl}
//             >
//               <Copy size={18} />
//               <span>Copy</span>
//             </button>
//           </div>
//         </div>

//         {/* Action Buttons */}
//         <div style={{ display: 'flex', gap: '12px' }}>
//           <button 
//             className="btn btn-primary"
//             style={{ 
//               flex: 1,
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//               gap: '10px',
//               padding: '14px',
//               fontWeight: '600',
//               fontSize: '15px'
//             }}
//             onClick={handleDownload}
//           >
//             <Download size={20} />
//             <span>Download QR</span>
//           </button>
//           <button 
//             className="btn"
//             style={{ 
//               flex: 1,
//               background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
//               color: '#495057',
//               display: 'flex',
//               alignItems: 'center',
//               justifyContent: 'center',
//               gap: '10px',
//               padding: '14px',
//               fontWeight: '600',
//               fontSize: '15px',
//               border: '2px solid #e8ecf1'
//             }}
//             onClick={handlePrint}
//           >
//             <Printer size={20} />
//             <span>Print</span>
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default QRCodeModal